/**
 * EULUMDAT (LDT) parser.
 */
import { LdcParsed } from './ldcTypes';

function ldtNum(v: string | undefined): number {
  if (v === undefined) return 0;
  const n = parseFloat(v.trim().replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

const norm360 = (a: number) => ((a % 360) + 360) % 360;

/** Number of C-planes actually stored in the file for a given symmetry indicator (Isym). */
function storedPlaneCount(isym: number, mc: number): number {
  switch (isym) {
    case 1:
      return 1;
    case 2:
    case 3:
      return Math.min(mc, Math.floor(mc / 2) + 1);
    case 4:
      return Math.min(mc, Math.floor(mc / 4) + 1);
    default:
      return mc;
  }
}

/** Map any C-angle to the equivalent angle inside the stored range for the given symmetry. */
function reduceCAngle(isym: number, c: number, firstStoredAngle: number): number {
  c = norm360(c);
  switch (isym) {
    case 1:
      return firstStoredAngle;
    case 2:
      return c <= 180 ? c : 360 - c;
    case 3:
      return c >= 90 && c <= 270 ? c : norm360(180 - c);
    case 4:
      if (c <= 90) return c;
      if (c <= 180) return 180 - c;
      if (c <= 270) return c - 180;
      return 360 - c;
    default:
      return c;
  }
}

/**
 * Parse a EULUMDAT (LDT) file into the shared LDC structure so the same unpack/mesh pipeline
 * can be used as for IES.
 *
 * LDT layout (one value per line):
 *  1 company, 2 Ityp, 3 Isym, 4 Mc, 5 Dc, 6 Ng, 7 Dg, 8-12 text, 13-25 numeric luminaire data,
 *  26 n (lamp sets), then 6 lines per lamp set, then 10 direct ratios,
 *  then Mc C-angles, Ng gamma angles and Mc1 * Ng intensities (cd/klm).
 */
export function parseLDT(text: string): LdcParsed {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');

  const isym = Math.floor(ldtNum(lines[2]));
  const mc = Math.max(0, Math.floor(ldtNum(lines[3])));
  const ng = Math.max(0, Math.floor(ldtNum(lines[5])));
  if (mc === 0 || ng === 0) {
    throw new Error('Invalid LDT: missing C-plane or gamma angle count');
  }

  const nLampSets = Math.max(1, Math.abs(Math.floor(ldtNum(lines[25]))));
  let totalFlux = 0;
  for (let s = 0; s < nLampSets; s++) {
    totalFlux += Math.abs(ldtNum(lines[26 + s * 6 + 2]));
  }
  let idx = 26 + nLampSets * 6 + 10; // skip lamp sets and direct ratios

  const cAngles: number[] = [];
  for (let k = 0; k < mc; k++) cAngles.push(ldtNum(lines[idx++]));
  const gAngles: number[] = [];
  for (let k = 0; k < ng; k++) gAngles.push(ldtNum(lines[idx++]));

  // Read the stored (possibly partial) C-planes
  const stored: number[][] = [];
  for (let c = 0; c < storedPlaneCount(isym, mc); c++) {
    const row: number[] = [];
    for (let g = 0; g < ng; g++) row.push(ldtNum(lines[idx++]));
    stored.push(row);
  }

  // Stored planes start at C=0 except for Isym=3, where they start at C=90
  let storedStart = 0;
  if (isym === 3) {
    const i90 = cAngles.findIndex((a) => a >= 90 - 1e-6);
    storedStart = i90 >= 0 ? i90 : 0;
  }
  const storedAngles = stored.map((_, i) => cAngles[(storedStart + i) % mc] ?? 0);

  const lookup = (c: number): number[] => {
    const target = reduceCAngle(isym, c, storedAngles[0] ?? 0);
    let best = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < storedAngles.length; i++) {
      const diff = Math.abs(norm360(storedAngles[i]! - target + 180) - 180);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    return stored[best] ?? [];
  };

  // Full C-plane set; make sure the grid closes at 360 so unpack treats it as a full sweep
  let fullAngles = cAngles.slice();
  if (isym !== 0 && (fullAngles.length <= 1 || fullAngles[fullAngles.length - 1]! < 360 - 1e-6)) {
    const dc = ldtNum(lines[4]) || (fullAngles.length > 1 ? fullAngles[1]! - fullAngles[0]! : 0) || 10;
    fullAngles = [];
    for (let a = 0; a <= 360 + 1e-6; a += dc) fullAngles.push(a);
  }
  const I = fullAngles.map((c) => lookup(c).slice());

  // Intensities are in cd/klm -> scale to absolute candela via the total luminous flux
  const candelaMultiplier = totalFlux > 0 ? totalFlux / 1000 : 1;

  return {
    verticalAnglesNum: ng,
    horizontalAnglesNum: fullAngles.length,
    candelaMultiplier,
    verticalAngles: gAngles,
    horizontalAngles: fullAngles,
    I,
  };
}
